import { apiFetch } from './client';
import { putToPresignedUrl } from './uploads';

export function normalizeCategory(raw = {}) {
  const src = raw?.category || raw || {};
  return {
    id: src.category_id || src.id || '',
    name: src.name || '',
    status: src.status || 'ACTIVE',
    sortOrder: src.sort_order ?? 0,
    emoji: src.emoji || '📦',
    imageUrl: src.image_url || src.imageUrl || src.tile_url || '',
  };
}

function categoryList(data) {
  if (Array.isArray(data)) return data;
  return data?.categories || data?.items || [];
}

export async function listCategories(token, { includeInactive } = {}) {
  const query = includeInactive ? '?include_inactive=1' : '';
  const data = await apiFetch(`/categories${query}`, { token });
  return categoryList(data).map(normalizeCategory);
}

export async function getCategory(id, token) {
  try {
    const raw = await apiFetch(`/categories/${encodeURIComponent(id)}`, { token });
    return normalizeCategory(raw);
  } catch {
    const all = await listCategories(token, { includeInactive: true });
    return all.find((c) => c.id === id) || normalizeCategory({ id });
  }
}

export async function createCategory(data, token) {
  const payload = {
    name: data.name,
    category_id: data.categoryId || undefined,
    sort_order: data.sortOrder ?? undefined,
    status: data.status || undefined,
    emoji: data.emoji || undefined,
  };
  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);
  const raw = await apiFetch('/categories', { method: 'POST', token, body: payload });
  return normalizeCategory(raw);
}

export async function updateCategory(id, patch, token) {
  const payload = {
    name: patch.name || undefined,
    sort_order: patch.sortOrder ?? undefined,
    status: patch.status || undefined,
    emoji: patch.emoji || undefined,
  };
  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);
  const raw = await apiFetch(`/categories/${encodeURIComponent(id)}`, { method: 'PATCH', token, body: payload });
  return normalizeCategory(raw);
}

export async function deleteCategory(id, token) {
  await apiFetch(`/categories/${encodeURIComponent(id)}`, { method: 'DELETE', token });
}

async function presignCategoryImage(id, contentType, token) {
  const raw = await apiFetch(`/categories/${encodeURIComponent(id)}/image/presign`, {
    method: 'POST',
    token,
    body: { content_type: contentType },
  });
  return { uploadUrl: raw.upload_url, s3Key: raw.s3_key };
}

export async function uploadCategoryImage(id, file, token) {
  const { uploadUrl, s3Key } = await presignCategoryImage(id, file.type || 'image/jpeg', token);
  await putToPresignedUrl(uploadUrl, file);
  const raw = await apiFetch(`/categories/${encodeURIComponent(id)}/image/confirm`, {
    method: 'POST',
    token,
    body: { s3_key: s3Key },
  });
  const confirmed = normalizeCategory(raw || { id });
  if (confirmed.imageUrl) return { ...confirmed, id: confirmed.id || id };
  const fresh = await getCategory(id, token);
  return { ...fresh, id: fresh.id || id };
}

export async function deleteCategoryImage(id, token) {
  const raw = await apiFetch(`/categories/${encodeURIComponent(id)}/image`, {
    method: 'DELETE',
    token,
  });
  const cleared = normalizeCategory(raw || { id });
  return { ...cleared, id: cleared.id || id, imageUrl: '' };
}
